import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSpinner,
  IonTextarea,
  IonToast,
  IonToggle,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import SelectItem from "./SelectItem";
import {
  getInventoryItemsCatalog,
  getInventoryLocationByIdFull,
  getMyInventoryLocations,
  MY_INVENTORY_LOCATIONS_LIMIT,
  postInventoryMovement,
} from "../services/inventoryLocation.service";
import { userProfileState } from "../services/state.service";

function locationLabel(loc: any) {
  return loc?.businessName ?? loc?.name ?? "";
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

// Lets a ground salesperson transfer stock between two of their own
// channel-partner locations. Both From and To pickers are populated from
// getMyInventoryLocations, i.e. whatever fyllo-core's
// TerritoryInventoryScopeInterceptor scopes reads to for this caller --
// which, same as the distributor picker on the retailer-creation flow,
// includes any DISTRIBUTOR org-wide (its read-only exemption), not just
// ones in the caller's own territories. InventoryMovement.create has no
// such exemption (interceptMovement checks only callerTerritoryIds.includes(
// location.territoryId)), so picking a distributor outside the caller's
// territories here is possible but will 403 on submit -- an accepted,
// narrow edge case rather than one this picker tries to pre-filter, since
// doing that client-side would mean re-deriving the caller's own
// territoryIds (from the cached profile, which can go stale -- see
// getMyInventoryLocations' doc comment) instead of trusting the backend.
// Plain state + inline validation, matching LocationOrders.tsx's
// lighter-weight approach for this kind of simple, single-phase form.
//
// No general movement-type selector (unlike fyllo-dashboard's admin form)
// -- every other type (PRODUCTION, REPAIR, INSTALLATION, etc.) belongs to
// flows this two-location form doesn't model. The one exception is the
// "Fulfills order" toggle below: when the To location has something
// outstanding on order for the selected item, this transfer can be typed
// ORDER_FULFILLMENT instead of the default INTERNAL_TRANSFER, so the
// backend decrements that location's on-order count instead of leaving it
// stuck even after the stock physically arrives.
const MoveStockForm: React.FC = () => {
  const userProfile: any = useRecoilValue(userProfileState);

  const [locations, setLocations] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [fromLocationId, setFromLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [date, setDate] = useState(todayIsoDate());
  const [comments, setComments] = useState("");
  const [moving, setMoving] = useState(false);

  const [fromStock, setFromStock] = useState<Record<string, number>>({});
  const [toOrder, setToOrder] = useState<Record<string, number>>({});
  const [fulfillsOrder, setFulfillsOrder] = useState(true);

  const [showSelectFromLocationModal, setShowSelectFromLocationModal] = useState(false);
  const [showSelectToLocationModal, setShowSelectToLocationModal] = useState(false);
  const [showSelectItemModal, setShowSelectItemModal] = useState(false);

  const [onSuccess, setOnSuccess] = useState("");
  const [onError, setOnError] = useState("");

  // A result hitting the query limit exactly means there could be more --
  // getMyInventoryLocations has no pagination today, so this is surfaced
  // rather than silently leaving some of the caller's own locations
  // unselectable with no indication why.
  const [possiblyTruncated, setPossiblyTruncated] = useState(false);

  useEffect(() => {
    if (!userProfile?.farmUserId) return;
    setLoading(true);
    Promise.all([
      getMyInventoryLocations().then((res) => {
        const arr = Array.isArray(res) ? res : [];
        setLocations(arr);
        setPossiblyTruncated(arr.length >= MY_INVENTORY_LOCATIONS_LIMIT);
      }),
      getInventoryItemsCatalog().then((res) => setItems(Array.isArray(res) ? res : [])),
    ])
      .catch((err) => setOnError(err?.message ?? String(err)))
      .finally(() => setLoading(false));
  }, [userProfile?.farmUserId]);

  // Surface what's actually available at the From location before the
  // salesperson picks a quantity -- the backend already rejects an oversell
  // (fromLocation.stock[itemId] < quantity), but only after submission; this
  // shows it up front instead, matching LocationOrders.tsx's Current Stock.
  useEffect(() => {
    if (!fromLocationId) {
      setFromStock({});
      return;
    }
    getInventoryLocationByIdFull(fromLocationId)
      .then((data) => setFromStock(data?.stock ?? {}))
      .catch(() => setFromStock({}));
  }, [fromLocationId]);

  // Surfaces whether the To location has anything outstanding on order, so
  // the salesperson can flag this transfer as fulfilling it (see
  // fulfillsOrder below) -- without this, a distributor-to-retailer
  // transfer completed here would silently never clear the retailer's "on
  // order" count, since only movements typed ORDER_FULFILLMENT do that.
  useEffect(() => {
    if (!toLocationId) {
      setToOrder({});
      return;
    }
    getInventoryLocationByIdFull(toLocationId)
      .then((data) => setToOrder(data?.order ?? {}))
      .catch(() => setToOrder({}));
  }, [toLocationId]);

  const hasOutstandingOrder = !!itemId && (toOrder[itemId] ?? 0) > 0;

  const canSubmit =
    !!fromLocationId &&
    !!toLocationId &&
    fromLocationId !== toLocationId &&
    !!itemId &&
    !!quantity &&
    Number(quantity) > 0;

  function selectFromLocation(loc: any) {
    setFromLocationId(loc.id);
    setShowSelectFromLocationModal(false);
  }

  function selectToLocation(loc: any) {
    setToLocationId(loc.id);
    setShowSelectToLocationModal(false);
  }

  function selectMoveItem(item: any) {
    setItemId(item.id);
    setShowSelectItemModal(false);
  }

  function moveStock() {
    if (!fromLocationId || !toLocationId) {
      setOnError("Select both a From and a To location");
      return;
    }
    if (fromLocationId === toLocationId) {
      setOnError("From and To locations must be different");
      return;
    }
    if (!itemId || !quantity || Number(quantity) <= 0) {
      setOnError("Select an item and enter a quantity greater than 0");
      return;
    }
    setMoving(true);
    postInventoryMovement({
      // Same fix as LocationOrders.tsx -- the backend's `date` property
      // validates as full ISO date-time, not the plain "YYYY-MM-DD" an
      // <input type="date"> gives.
      date: new Date(date).toISOString(),
      quantity: Number(quantity),
      fromInventoryLocationId: fromLocationId,
      toInventoryLocationId: toLocationId,
      inventoryItemId: itemId,
      comments: comments || undefined,
      type: hasOutstandingOrder && fulfillsOrder ? "ORDER_FULFILLMENT" : undefined,
    })
      .then(() => {
        setOnSuccess("Stock moved");
        // Keep From/To selected -- a salesperson doing several transfers
        // between the same two locations shouldn't have to re-pick them
        // every time.
        setItemId("");
        setQuantity("");
        setComments("");
      })
      .catch((err: any) => setOnError(err?.message ?? String(err)))
      .finally(() => setMoving(false));
  }

  if (loading) {
    return <IonSpinner name="bubbles" />;
  }

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Move Stock</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {possiblyTruncated && (
          <IonItem lines="none" color="warning">
            <IonLabel class="ion-text-wrap">
              Showing the first {MY_INVENTORY_LOCATIONS_LIMIT} locations you can access -- yours may not all be listed below.
            </IonLabel>
          </IonItem>
        )}
        <IonList>
          <IonItem>
            <IonLabel position="stacked">From Location</IonLabel>
            <IonInput
              readonly
              value={locationLabel(locations.find((loc) => loc.id === fromLocationId))}
              placeholder="Select location"
              onClick={() => setShowSelectFromLocationModal(true)}
            ></IonInput>
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">To Location</IonLabel>
            <IonInput
              readonly
              value={locationLabel(locations.find((loc) => loc.id === toLocationId))}
              placeholder="Select location"
              onClick={() => setShowSelectToLocationModal(true)}
            ></IonInput>
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Item</IonLabel>
            <IonInput
              readonly
              value={items.find((item) => item.id === itemId)?.name ?? ""}
              placeholder="Select item"
              onClick={() => setShowSelectItemModal(true)}
            ></IonInput>
          </IonItem>
          {fromLocationId && itemId && (
            <IonItem lines="none">
              <IonLabel class="secondary-text">Available at From Location: {fromStock[itemId] ?? 0}</IonLabel>
            </IonItem>
          )}
          {hasOutstandingOrder && (
            <IonItem>
              <IonLabel>
                Fulfills order
                <p className="secondary-text">To Location has {toOrder[itemId] ?? 0} on order for this item</p>
              </IonLabel>
              <IonToggle checked={fulfillsOrder} onIonChange={(e) => setFulfillsOrder(e.detail.checked)} />
            </IonItem>
          )}

          <IonItem>
            <IonLabel position="stacked">Quantity</IonLabel>
            <IonInput type="number" min="1" value={quantity} onIonChange={(e) => setQuantity(e.detail.value ?? "")}></IonInput>
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Date</IonLabel>
            <IonInput type="date" value={date} onIonChange={(e) => setDate(e.detail.value ?? todayIsoDate())}></IonInput>
          </IonItem>

          <IonItem lines="none">
            <IonLabel position="stacked">Comments</IonLabel>
            <IonTextarea value={comments} onIonChange={(e) => setComments(e.detail.value ?? "")}></IonTextarea>
          </IonItem>
        </IonList>

        <IonButton expand="block" disabled={moving || !canSubmit} onClick={moveStock}>
          {moving ? <IonSpinner name="bubbles" /> : "Move Stock"}
        </IonButton>
      </IonCardContent>

      <IonToast isOpen={!!onSuccess} onDidDismiss={() => setOnSuccess("")} message={onSuccess} duration={2000} color="success" />
      <IonToast isOpen={!!onError} onDidDismiss={() => setOnError("")} message={onError} duration={2500} color="danger" />

      {showSelectFromLocationModal && (
        <IonModal isOpen={showSelectFromLocationModal}>
          <SelectItem
            label="Select From Location"
            items={locations.filter((loc) => loc.id !== toLocationId)}
            labelKey="name"
            valueKey="id"
            setItem={selectFromLocation}
            selectedValue={fromLocationId}
            onClose={setShowSelectFromLocationModal}
          />
        </IonModal>
      )}

      {showSelectToLocationModal && (
        <IonModal isOpen={showSelectToLocationModal}>
          <SelectItem
            label="Select To Location"
            items={locations.filter((loc) => loc.id !== fromLocationId)}
            labelKey="name"
            valueKey="id"
            setItem={selectToLocation}
            selectedValue={toLocationId}
            onClose={setShowSelectToLocationModal}
          />
        </IonModal>
      )}

      {showSelectItemModal && (
        <IonModal isOpen={showSelectItemModal}>
          <SelectItem
            label="Select Item"
            items={items}
            labelKey="name"
            valueKey="id"
            setItem={selectMoveItem}
            selectedValue={itemId}
            onClose={setShowSelectItemModal}
          />
        </IonModal>
      )}
    </IonCard>
  );
};

export default MoveStockForm;
